import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ILightWeightObject } from '../../../shared/models';
import { FiDialogsService } from '../../../shared/fi-dialogs';
import { IPKSettings, IPKUnitExternal, IPKUnitMatch, IPKWorkplaceExternal } from '../pk';
import { BehaviorSubject, Subject } from 'rxjs';
import { PkService } from '../pk.service';
import { FiSnackBarService } from '../../../shared/fi-snackbar/snack-bar.service';
import { AppTextService } from '../../../shared/app-text';
import { FiCustomerDataService } from '../../../shared/fi-shared-services/fi-customer-data.service';
import { SaveChangesWarningService } from '../../../shared/fi-shared-services/save-changes-warning.service';
import { PKUnitsImportType } from '@fi-app/external-platform-integration/pk/pk-units-import-type';

@Component({
    selector: 'pk-units-matching',
    templateUrl: './pk-units-matching.component.html',
    styleUrls: ['./pk-units-matching.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'pk-units-matching' },
    standalone: false,
})
export class PkUnitsMatchingComponent implements OnInit, OnDestroy {
    @Input() pkSettings: IPKSettings;
    @Input() businessUnits: ILightWeightObject[];
    @Input() existingUnitMatches: IPKUnitMatch[];

    pkUnitMatches$ = new BehaviorSubject<IPKUnitMatch[]>([]);
    isBusinessUnitDisabledFn;
    private hasChanges = false;

    constructor(
        private pkService: PkService,
        private fiDialogsService: FiDialogsService,
        private fiSnackBarService: FiSnackBarService,
        private appTextService: AppTextService,
        private customerDataService: FiCustomerDataService,
        private saveChangesWarningService: SaveChangesWarningService,
    ) {}

    ngOnInit(): void {
        this.pkUnitMatches$.next(this.existingUnitMatches);
        this.isBusinessUnitDisabledFn = this.isBusinessUnitDisabled.bind(this);

        this.saveChangesWarningService.start(() => this.hasChanges);
    }

    getDisplayName(): string {
        return this.customerDataService.customerData.DisplayName;
    }

    inputFileChange($event: Event, input: HTMLInputElement) {
        const inputElement = $event.target as HTMLInputElement;
        this.pkService
            .getUnitMatchesFromFile(inputElement.files)
            .then((data) => {
                this.pkUnitMatches$.next(this.getPKUnitMatches(data));
                input.value = '';
                this.hasChanges = false;
            })
            .catch(() => {
                this.fiDialogsService.error({
                    headerText: this.appTextService.get('pk.matching_units_input_error_header'),
                    messageText: this.appTextService.get('pk.matching_units_input_error_message'),
                    yesButtonText: this.appTextService.get('done'),
                });
                //this.pkUnitMatches$.next([]);
                input.value = '';
            });
    }

    private getPKUnitMatches(data: IPKUnitExternal[]): IPKUnitMatch[] {
        const isWorkplaceMode = this.pkSettings.UnitsImportType === PKUnitsImportType.Workplace;
        const pkUnitMatches: IPKUnitMatch[] = [];

        data.forEach((pkUnit) => {
            const workplaces: IPKWorkplaceExternal[] = isWorkplaceMode ? pkUnit.workplace_ids : [null];

            workplaces.forEach((workplace) => {
                const displayName = isWorkplaceMode
                    ? `${pkUnit.display_name} (${workplace.description})`
                    : pkUnit.display_name;
                const workplaceUrl = isWorkplaceMode
                    ? `https://personalkollen.se/api/workplaces/${workplace.id}/`
                    : undefined;
                const existingUnitMatch = this.existingUnitMatches.find(
                    (um) => um.Url === pkUnit.url && (!isWorkplaceMode || um.WorkplaceUrl == workplaceUrl),
                );
                const pkUnitMatch: IPKUnitMatch = {
                    ApiKey: pkUnit.api_key,
                    DisplayName: displayName,
                    OrganisationNumber: pkUnit.organisation_number,
                    Url: pkUnit.url,
                    WorkplaceUrl: workplaceUrl,
                    BusinessUnitId: existingUnitMatch ? existingUnitMatch.BusinessUnitId : null,
                };
                pkUnitMatches.push(pkUnitMatch);
            });
        });

        return pkUnitMatches;
    }

    onBusinessUnitIdChanged(pkUnit: IPKUnitMatch) {
        this.hasChanges = true;
    }

    private isBusinessUnitDisabled(businessUnit: ILightWeightObject, selectedBusinessUnitId: number): boolean {
        return (
            businessUnit.Id !== selectedBusinessUnitId &&
            !!this.pkUnitMatches$.value.find((item) => item.BusinessUnitId === businessUnit.Id)
        );
    }

    saveUnitMatches() {
        this.pkService.saveUnitMatches(this.pkUnitMatches$.value).then(() => {
            this.existingUnitMatches = this.pkUnitMatches$.value;
            this.showSnackBar();
            this.hasChanges = false;
        });
    }

    private showSnackBar(): void {
        this.fiSnackBarService.showSnackbar({
            textMessage: this.appTextService.get('pk.snackbar_matching_units'),
            buttonText: this.appTextService.get('common.dismiss'),
        });
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
